import * as React from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

export interface AlertOptions {
  catchOnCancel?: boolean;
  title: string;
  body: string;
  alertVariant?: "alert" | "confirmation";
}

const AlertServiceContext = React.createContext<
  (options: AlertOptions) => Promise<void>
>(Promise.reject);

export const useAlert = () =>
  React.useContext(AlertServiceContext);

export const AlertServiceProvider = ({ children }: {children: any}) => {
  const { t } = useTranslation();

  const [
    alertState,
    setAlertState
  ] = React.useState<AlertOptions | null>(null);

  const awaitingPromiseRef = React.useRef<{
    resolve: () => void;
    reject: () => void;
  }>();

  const openAlert = (options: AlertOptions) => {
    setAlertState(options);
    return new Promise<void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const handleClose = () => {
    if (alertState && alertState.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.reject();
    }

    setAlertState(null);
  };

  const handleSubmit = () => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve();
    }

    setAlertState(null);
  };

  return (
    <>
      <AlertServiceContext.Provider
        value={openAlert}
        children={children}
      />
      <Modal
        centered
        show={Boolean(alertState)}
        onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{alertState?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {alertState?.body}
          </p>
        </Modal.Body>
        <Modal.Footer>
          {(alertState?.alertVariant === "confirmation")?(
            <>
              <Button variant="outline-primary" onClick={handleClose}>{t("cancel")}</Button>
              <Button variant="primary" onClick={handleSubmit}>{t("confirm")}</Button>
            </>
          ):(
            <Button variant="outline-primary" onClick={handleClose}>{t("dismiss")}</Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
